/**
 * E.164 phone normalization, Estonia-first. The one copy.
 *
 * Register and scraped numbers arrive in every imaginable shape: "5551 2345",
 * "+372 5551 2345", "003725551234", "tel: 644 1234". The dialable output is
 * always "+<countrycode><digits>" or null when the input cannot be a phone
 * number.
 *
 * WHY THIS LIVES IN packages/core AND NOT IN THE MODULE THAT USES IT. Until
 * this file existed the same rules were written twice, once in
 * `apps/api/src/modules/crm-calls/lib/phone.ts` and once in
 * `apps/web_crm/src/lib/phone.ts`, and the second one said in its own header
 * that it "only has to be good enough". Both sides agreed at the time they were
 * split, which is the whole danger: nothing fails on the day the copy is made.
 * It fails later, quietly, when one side gains a rule the other does not.
 *
 * The failure is invisible from either end. The browser normalizes a number the
 * operator typed, sends it, and the server normalizes the same number
 * differently - so the row is written under one key and looked up under
 * another. Nobody sees an error. The inbound blocklist is the sharpest case:
 * the operator blocks a caller, the entry appears on the screen exactly as
 * expected, and the caller keeps getting through, because the webhook compares
 * against a key the browser never produced.
 *
 * So the rule is not "share code because duplication is untidy". It is that two
 * copies of a COMPARISON KEY cannot be allowed to disagree, and the only way to
 * guarantee that is for there to be one of them.
 *
 * PURE ON PURPOSE: zero imports of any kind. It runs inside a Fastify webhook,
 * inside a browser event handler and inside a `node:test` process, so anything
 * that ties it to one of those would break the other two. This matches the
 * other pure modules here (`blog-route.ts`, `themeSurface.ts`), which say the
 * same thing in their own headers.
 *
 * Consumers import the `@wa/core/phone` subpath rather than the `@wa/core`
 * barrel. That is the convention already in force across the repo - `theme`,
 * `locale-url`, `blog-route` and `version` are all reached by subpath - and it
 * keeps a caller that wants one function from depending on the whole shared
 * surface. To be accurate about the reason: this package's barrel is itself
 * harmless, since `index.ts` re-exports only `i18n.ts` and `calc.ts` and both
 * are import-free. The barrel that swallows a component tree and kills a node
 * test on a CSS import is the folder-level `index.ts` inside the apps, not this
 * one.
 *
 * NOT THE SAME THING AS `crm-calls/lib/phone-key.ts`, which deliberately
 * disagrees with this file. That one reduces a number to a comparison key for
 * reverse lookup over free-text columns, has to be expressible in SQL so the
 * lookup can ride a functional index, and therefore leaves a bare local number
 * bare. This one turns a bare local number into a dialable +372 address. Both
 * are correct for their own job and neither may be swapped for the other.
 */

/** Strict E.164: plus, non-zero lead digit, 7-15 digits total. */
const E164_RE = /^\+[1-9]\d{6,14}$/;

/** Estonian subscriber numbers are 7 or 8 digits (landline 7, mobile 7-8). */
const EE_LOCAL_RE = /^[1-9]\d{6,7}$/;

export function isE164(value: string): boolean {
  return E164_RE.test(value);
}

/**
 * Normalize a raw phone string to E.164. `null` = not usable as a dial target.
 * Bare local numbers get the default country's prefix (+372 for "EE").
 */
export function normalizeE164(raw: string, defaultCountry: "EE" = "EE"): string | null {
  if (typeof raw !== "string") return null;
  // Drop label prefixes ("tel:", "Tel."), keep digits and a leading plus.
  let s = raw.trim().replace(/^(?:tel|telefon|phone|mob|gsm)[.:]?\s*/i, "");
  s = s.replace(/[\s\-(). /]+/g, "");
  if (!s) return null;

  if (s.startsWith("00")) s = `+${s.slice(2)}`;

  if (s.startsWith("+")) {
    const digits = s.slice(1);
    if (!/^\d+$/.test(digits)) return null;
    const candidate = `+${digits}`;
    return E164_RE.test(candidate) ? candidate : null;
  }

  if (!/^\d+$/.test(s)) return null;

  if (defaultCountry === "EE") {
    // "372..." written without the plus.
    if (s.startsWith("372") && EE_LOCAL_RE.test(s.slice(3))) return `+${s}`;
    if (EE_LOCAL_RE.test(s)) return `+372${s}`;
  }
  return null;
}

/** Display formatting: groups of 4 after the country code, e.g. "+372 5551 2345". */
export function formatPhone(e164: string): string {
  if (!E164_RE.test(e164)) return e164;
  const m = /^\+(372|371|370|358|46|47|45|49|44|7|1)(\d+)$/.exec(e164);
  const cc = m?.[1];
  const rest = m?.[2];
  if (!cc || !rest) return e164;
  const groups = rest.replace(/(\d{4})(?=\d)/g, "$1 ").trim();
  return `+${cc} ${groups}`;
}
